import React, { FC, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
  TextField,
  Button,
} from "@mui/material";
import styles from "./IssueCredits.module.scss";
import LayoutHeader from "@features/common/header/layoutHeader/LayoutHeader";
import ExcelUploadProgress from "@features/issueCredits/excelUploadProgress/ExcelUploadProgress";
import PageLoader from "@features/common/pageLoader/PageLoader";
import { DownloadIcon } from "customIcons";
import {
  useFetchPromotionsListQuery,
  useUploadExcelFileMutation,
  useLazyGetSampleFileQuery,
  useCreatePromotionMutation,
} from "@features/issueCredits/issueCreditsAPI";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import {
  notifyUser,
  selectDashboardData,
  setUploadListSnapshot,
} from "@redux/slices/CommonSlice";
import { PAGE_URLS } from "@constants/common";

// ============================================================================
// Types & Constants
// ============================================================================
type UploadStatus = "idle" | "uploading" | "success" | "error";
type ActiveTab = "createPromotion" | "fileUpload";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const VALID_FILE_EXTENSIONS = [".xlsx", ".xls", ".csv", ".tsv"];
const MAX_PROMOTION_NAME_LENGTH = 50;
const UPLOAD_PROGRESS_INTERVAL = 200; // ms
const UPLOAD_SUCCESS_DELAY = 1500; // ms
const ERROR_DISPLAY_DELAY = 200; // ms

const IssueCredits: FC = () => {
  // ==========================================================================
  // Hooks
  // ==========================================================================
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dashboardData = useAppSelector(selectDashboardData);

  // ==========================================================================
  // Tab & UI State
  // ==========================================================================
  const [activeTab, setActiveTab] = useState<ActiveTab>("createPromotion");
  const [dragActive, setDragActive] = useState(false);

  // ==========================================================================
  // Promotion State
  // ==========================================================================
  const [promotions, setPromotions] = useState<any[]>([]);
  const [selectedPromotion, setSelectedPromotion] = useState<string>("");
  const [nextPageUrl, setNextPageUrl] = useState<string | null>(null);
  const [currentFetchUrl, setCurrentFetchUrl] = useState<string | null>(null);
  const [promotionName, setPromotionName] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<{
    code?: string[];
    name?: string[];
  } | null>(null);

  // ==========================================================================
  // Upload State
  // ==========================================================================
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>("idle");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFileName, setCurrentFileName] = useState("");
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [downloadingSample, setDownloadingSample] = useState(false);

  // ==========================================================================
  // API Hooks
  // ==========================================================================
  const {
    data: initialData,
    refetch: refetchPromotions,
    isLoading: isLoadingPromotions,
  } = useFetchPromotionsListQuery();

  const { data: nextPageData, isFetching: isFetchingNextPage } =
    useFetchPromotionsListQuery(currentFetchUrl ?? undefined, {
      skip: !currentFetchUrl,
    });

  const [uploadExcel] = useUploadExcelFileMutation();
  const [triggerSampleDownload] = useLazyGetSampleFileQuery();
  const [createPromotion, { isLoading: isCreating }] =
    useCreatePromotionMutation();

  // ==========================================================================
  // Computed Values
  // ==========================================================================
  const hasPromotions = promotions && promotions.length > 0;
  const isFileUploadDisabled = !hasPromotions || !selectedPromotion;
  const isSubmitEnabled =
    promotionName.trim() !== "" && code.trim() !== "" && !isCreating;

  // ==========================================================================
  // Effects
  // ==========================================================================
  /**
   * Load initial promotions data
   */
  useEffect(() => {
    if (initialData?.data) {
      setPromotions(initialData.data);
      setNextPageUrl(initialData.next || null);
    }
  }, [initialData]);

  /**
   * Handle pagination - append next page data
   */
  useEffect(() => {
    if (nextPageData?.data && currentFetchUrl) {
      setPromotions((prev) => [...prev, ...nextPageData.data]);
      setNextPageUrl(nextPageData.next || null);
      setCurrentFetchUrl(null);
    }
  }, [nextPageData, currentFetchUrl]);

  // ==========================================================================
  // File Validation
  // ==========================================================================
  /**
   * Validates file extension and size
   * @param {File} file - File to validate
   * @returns {boolean} True if file is valid
   */
  const isValidFile = (file: File): boolean => {
    const fileName = file.name.toLowerCase();
    const hasValidExtension = VALID_FILE_EXTENSIONS.some((ext) =>
      fileName.endsWith(ext),
    );

    if (!hasValidExtension) {
      dispatch(
        notifyUser({
          message: `Please upload only ${VALID_FILE_EXTENSIONS.join(
            ", ",
          )} files`,
          severity: "error",
        }),
      );
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      dispatch(
        notifyUser({
          message: "File size should not exceed 10MB",
          severity: "error",
        }),
      );
      return false;
    }

    return true;
  };

  // ==========================================================================
  // Upload Helpers
  // ==========================================================================
  /**
   * Simulates upload progress (stops at 90% until actual completion)
   * @returns {NodeJS.Timeout} Interval ID
   */
  const simulateProgress = (): NodeJS.Timeout => {
    setUploadProgress(0);
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 90) {
          clearInterval(interval);
          return 90;
        }
        return prev + 10;
      });
    }, UPLOAD_PROGRESS_INTERVAL);
    return interval;
  };

  // ==========================================================================
  // Tab Handlers
  // ==========================================================================
  /**
   * Handles tab change with validation
   * @param {ActiveTab} tab - Tab to switch to
   */
  const handleTabChange = (tab: ActiveTab) => {
    if (tab === "fileUpload" && isFileUploadDisabled) {
      dispatch(
        notifyUser({
          message: "Please create a promotion first before uploading files.",
          severity: "warning",
        }),
      );
      return;
    }
    setActiveTab(tab);
  };

  // ==========================================================================
  // Promotion Handlers
  // ==========================================================================
  /**
   * Creates a new promotion
   */
  const handleCreatePromotion = async () => {
    if (!isSubmitEnabled) return;
    setError(null);

    try {
      const res: any = await createPromotion({
        name: promotionName,
        code,
      }).unwrap();

      refetchPromotions();

      // Auto-select the newly created promotion
      if (res?.data?.id) {
        setSelectedPromotion(res.data.id.toString());
      }

      // Switch to file upload tab
      setActiveTab("fileUpload");

      // Reset form
      setPromotionName("");
      setCode("");

      dispatch(
        notifyUser({
          title: "Promotion Created",
          message:
            "New promotion is created and is automatically selected for this credit.",
        }),
      );
    } catch (err: any) {
      if (err?.data?.errors) {
        setError(err.data.errors);
      }
    }
  };

  const handlePromotionSelect = (value: string) => {
    setSelectedPromotion(value);

    // Switch to file upload tab when promotion is selected
    if (value) {
      setActiveTab("fileUpload");
    }
  };

  /**
   * Handles infinite scroll in promotion select menu
   * @param {React.UIEvent<HTMLUListElement>} event - Scroll event
   */
  const handleMenuScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const target = event.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = target;

    if (scrollHeight - scrollTop <= clientHeight + 10) {
      if (nextPageUrl && !currentFetchUrl && !isFetchingNextPage) {
        setCurrentFetchUrl(nextPageUrl);
      }
    }
  };

  // ==========================================================================
  // File Upload Handlers
  // ==========================================================================
  /**
   * Uploads file to server
   * @param {File} file - File to upload
   */
  const uploadFile = async (file: File) => {
    if (!selectedPromotion) {
      dispatch(
        notifyUser({
          message: "Please choose a promotion before uploading the file.",
          severity: "error",
        }),
      );
      return;
    }

    if (!isValidFile(file)) return;

    setCurrentFileName(file.name);
    setUploadStatus("uploading");
    setUploadError(null);
    const progressInterval = simulateProgress();

    try {
      const formData = new FormData();
      formData.append("document", file);
      formData.append("promotion", selectedPromotion);

      const result = await uploadExcel(formData).unwrap();

      // Store upload URLs in Redux
      dispatch(
        setUploadListSnapshot({
          cancel: result?.data?.api_urls.cancel,
          clear_all: result?.data?.api_urls?.clear_all,
          list: result?.data?.api_urls?.list,
          save: result?.data?.api_urls?.save,
          submit: result?.data?.api_urls?.submit,
          summary: result?.data?.api_urls?.summary,
        }),
      );

      clearInterval(progressInterval);
      setUploadProgress(100);
      setUploadStatus("success");

      dispatch(
        notifyUser({
          message: `${file.name} uploaded successfully.`,
          severity: "success",
        }),
      );

      // Navigate to credits listing after success
      setTimeout(() => {
        if (result?.data) {
          navigate(PAGE_URLS.CREDITS_LISTING);
        } else {
          window.location.reload();
        }
      }, UPLOAD_SUCCESS_DELAY);

      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error: any) {
      clearInterval(progressInterval);
      setUploadProgress(100);

      const errorMessage =
        error?.data?.errors?.document?.[0] ||
        error?.data?.message ||
        `${file.name} upload failed.`;

      setUploadStatus("error");

      // Show error state briefly, then display error message
      setTimeout(() => {
        setUploadStatus("idle");
        setUploadProgress(0);

        setTimeout(() => {
          setUploadError(errorMessage);
        }, ERROR_DISPLAY_DELAY);
      }, UPLOAD_SUCCESS_DELAY);
    }
  };

  /**
   * Handles file input change
   * @param {React.ChangeEvent<HTMLInputElement>} e - Change event
   */
  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    e.target.value = "";
  };

  /**
   * Opens file dialog
   */
  const openFileDialog = () => {
    if (uploadStatus === "uploading") return;
    fileInputRef.current?.click();
  };

  // ==========================================================================
  // Drag & Drop Handlers
  // ==========================================================================
  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragActive(true);
    }
  };

  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) uploadFile(droppedFile);
  };

  // ==========================================================================
  // Sample Download Handler
  // ==========================================================================
  /**
   * Downloads sample spreadsheet
   */
  const handleSampleDownloadClick = async () => {
    if (downloadingSample) return;

    setDownloadingSample(true);
    dispatch(
      notifyUser({
        message: "Downloading sample spreadsheet...",
        severity: "info",
      }),
    );

    try {
      const res = await triggerSampleDownload().unwrap();

      if (res?.data?.document) {
        window.open(res.data.document, "_self");
        dispatch(
          notifyUser({
            message: "Downloaded successfully.",
            severity: "success",
          }),
        );
      } else {
        dispatch(
          notifyUser({
            message: res?.message || "Download failed.",
            severity: "error",
          }),
        );
      }
    } catch (e: any) {
      dispatch(
        notifyUser({
          message: e?.data?.message || "Download failed.",
          severity: "error",
        }),
      );
    } finally {
      setDownloadingSample(false);
    }
  };

  // ==========================================================================
  // Loading State
  // ==========================================================================
  if (isLoadingPromotions) {
    return <PageLoader />;
  }

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <div className={styles.pageContainer}>
      {/* Header */}
      <LayoutHeader
        currency={dashboardData?.currency}
        amount={dashboardData?.amount}
        headerTitle="Issue Credits"
      />

      <div className={styles.contentCard}>
        {/* Tab Navigation */}
        <div className={styles.tabHeader}>
          <div
            className={`${styles.tab} ${
              activeTab === "createPromotion" ? styles.active : ""
            }`}
            onClick={() => handleTabChange("createPromotion")}
          >
            {promotions?.length !== 0
              ? "Create or Select Promotion"
              : "Create Promotion"}
          </div>
          <div
            className={`${styles.tab} ${
              activeTab === "fileUpload" ? styles.active : ""
            } ${isFileUploadDisabled ? styles.disabled : ""}`}
            onClick={() => handleTabChange("fileUpload")}
          >
            File Upload
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === "createPromotion" ? (
          /* ===== CREATE PROMOTION TAB ===== */
          <div
            className={`${styles.createPromotionSection} create-promo-section`}
          >
            {/* FIRST: Select Existing Promotion Section */}
            {promotions?.length !== 0 && (
              <>
                <h4 className={styles.selectExistingPromotion}>
                  Select an existing Promotion.
                </h4>
                <div
                  className={`${styles.promotionSelectWrapper} promotions-dropzone-section`}
                >
                  <FormControl fullWidth className={styles.promotionSelect}>
                    <InputLabel id="promotion-upload-select-label">
                      Select a Promotion
                    </InputLabel>
                    <Select
                      labelId="promotion-upload-select-label"
                      id="promotion-upload-select"
                      value={selectedPromotion}
                      label="Select Promotion"
                      onChange={(e) => handlePromotionSelect(e.target.value)}
                      MenuProps={{
                        PaperProps: {
                          onScroll: handleMenuScroll,
                          style: { maxHeight: 300 },
                        },
                      }}
                    >
                      {promotions.map((promo) => (
                        <MenuItem key={promo.id} value={promo.id}>
                          {promo.name}
                        </MenuItem>
                      ))}
                      {isFetchingNextPage && (
                        <MenuItem disabled>
                          <CircularProgress size={20} />
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </div>
                <div className={styles.orText}>Or</div>
              </>
            )}

            {/* SECOND: Create New Promotion Section */}
            <div className={styles.fileUploadTitle}>Create a New Promotion</div>

            <div className={styles.promotionInputs}>
              {/* Promotion Name Field */}
              <TextField
                fullWidth
                placeholder="Promotion Name"
                value={promotionName}
                onChange={(e) => setPromotionName(e.target.value)}
                className={styles.promoInputFields}
                error={!!error?.name}
                helperText={error?.name?.[0]}
                inputProps={{
                  maxLength: MAX_PROMOTION_NAME_LENGTH,
                }}
                InputProps={{
                  startAdornment: (
                    <img
                      src="/images/message-text.svg"
                      alt="Message Icon"
                      className={styles.inputIcon}
                    />
                  ),
                }}
              />

              {/* Code Field */}
              <TextField
                fullWidth
                placeholder="Code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className={styles.promoInputFields}
                error={!!error?.code}
                helperText={error?.code?.[0]}
                InputProps={{
                  startAdornment: (
                    <img
                      src="/images/message-notif.svg"
                      alt="Code Icon"
                      className={styles.inputIcon}
                    />
                  ),
                }}
              />
            </div>

            {/* Submit Button */}
            <div className={styles.buttonGroup}>
              <Button
                onClick={handleCreatePromotion}
                disabled={!isSubmitEnabled}
                className={`${styles.submit} create-promo-submit-button`}
                variant="contained"
              >
                {isCreating ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  "Submit"
                )}
              </Button>
            </div>
          </div>
        ) : (
          /* ===== FILE UPLOAD TAB ===== */
          <div
            className={`${styles.fileUploadSection} promotions-dropzone-section`}
          >
            <div className={styles.fileUploadTitle}>File Upload</div>
            <div className={styles.promotionSelectWrapper}>
              <FormControl fullWidth className={styles.promotionSelect}>
                <Select
                  labelId="promotion-upload-select-label"
                  id="promotion-upload-select"
                  value={selectedPromotion}
                  onChange={(e) => handlePromotionSelect(e.target.value)}
                  MenuProps={{
                    PaperProps: {
                      onScroll: handleMenuScroll,
                      style: { maxHeight: 300 },
                    },
                  }}
                >
                  {promotions.map((promo) => (
                    <MenuItem key={promo.id} value={promo.id}>
                      {promo.name}
                    </MenuItem>
                  ))}
                  {isFetchingNextPage && (
                    <MenuItem disabled>
                      <CircularProgress size={20} />
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </div>
            <div className={styles.uploadSection}>
              {/* File Drop Zone */}
              <div
                className={`${styles.fileDropZone} ${
                  dragActive ? styles.dragActive : ""
                } ${uploadStatus === "uploading" ? styles.uploading : ""}`}
                onDragEnter={onDragEnter}
                onDragLeave={onDragLeave}
                onDragOver={onDragOver}
                onDrop={onFileDrop}
                style={{
                  cursor:
                    uploadStatus === "uploading" ? "not-allowed" : "pointer",
                }}
              >
                <div
                  onClick={openFileDialog}
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <div className={styles.uploadIconContainer}>
                    <img
                      src="/images/document-upload.svg"
                      className={styles.uploadIcon}
                      alt="upload"
                    />
                  </div>

                  <div style={{ fontSize: "14px", fontWeight: "600" }}>
                    Choose a file or drag it here
                  </div>
                  <div
                    style={{
                      color: "grey",
                      fontSize: "0.85rem",
                      marginTop: 4,
                    }}
                  >
                    (Supported: {VALID_FILE_EXTENSIONS.join(", ")})
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  className={styles.hiddenFileInput}
                  accept={VALID_FILE_EXTENSIONS.join(",")}
                  onChange={onFileInputChange}
                  disabled={uploadStatus === "uploading"}
                  style={{ display: "none" }}
                />
              </div>

              {/* Sample Download */}
              <div
                className={styles.downloadTemplate}
                onClick={handleSampleDownloadClick}
                style={{
                  cursor: downloadingSample ? "not-allowed" : "pointer",
                  opacity: downloadingSample ? 0.6 : 1,
                  position: "relative",
                }}
              >
                <div className={styles.downloadIconContainer}>
                  <DownloadIcon />
                </div>
                <div>Download Spreadsheet</div>
                <div>Template</div>
                {downloadingSample && (
                  <CircularProgress
                    size={22}
                    style={{ position: "absolute", top: 8, right: 8 }}
                  />
                )}
              </div>
            </div>

            {/* Upload Progress */}
            {uploadStatus !== "idle" && (
              <ExcelUploadProgress
                uploadStatus={uploadStatus}
                uploadProgress={uploadProgress}
                currentFileName={currentFileName}
              />
            )}

            {/* Upload Error Message */}
            {uploadError && (
              <div className={styles.uploadErrorMessage}>
                <div className={styles.fileName}>
                  <img src="/images/attach-circle.svg" alt="" />
                  {currentFileName}
                </div>
                <div className={styles.errorText}>{uploadError}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default IssueCredits;
